import { MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from "react";
import { Button, Slider } from "antd";
import { CheckCircleOutlined, LoadingOutlined, MinusCircleOutlined, RollbackOutlined } from "@ant-design/icons";
import { useRecoilState } from "recoil";
import { useOnClickOutside } from "usehooks-ts";
import { DesignDto } from "../../../../../generated/dashboard-client";
import { BrandTemplateDto } from "../../../../../api/BrandTemplateApi";
import DesignApi from "../../../../../api/DesignApi";
import { DownloadType, MenuMode } from "../../../../design/DesignCardMenu";
import { memberState } from "../../../../../state/atoms";
import {
  getHeader,
  isCustomSize,
  isDigiboard,
  isDigital,
  isSocial,
} from "../../../../../util/utils";
import { isDesignSubscription, isFreeUser } from "../../../../../service/BillingService";
import EditDesignIcon from "../../../../icons/EditDesignIcon";
import CopyDesignIcon from "../../../../icons/CopyDesignIcon";
import CopyToBrandKitIcon from "../../../../icons/CopyToBrandKitIcon";
import DownloadDesignIcon from "../../../../icons/DownloadDesignIcon";
import DeleteDesignIcon from "../../../../icons/DeleteDesignIcon";
import CheckIcon from "../../../../icons/CheckIcon";
import ProIcon from "../../../../icons/ProIcon";
import "./BrandTemplateCardMenu.scss";

const PremiumDownloadTypes = [
  DownloadType.Standard,
  DownloadType.ProPrint,
  DownloadType.PremiumWeb,
  DownloadType.HDVideo,
];

export interface BrandTemplateCardMenuProps {
  template: BrandTemplateDto;
  productLabel: string;
  design: DesignDto | null;
  mode: MenuMode;
  setMode: (mode: MenuMode) => void;
  isCopyLoading: boolean;
  isDownloadFetching: boolean;
  onEdit: () => void;
  onCopy: () => void;
  onCopyToBrandKit: () => void;
  onDownloadClick: () => void;
  onRemoveFromBrandTemplates: () => void;
  onMakeLive: () => void;
  onRevertToDraft: () => void;
  onDelete: () => void;
  closeMenu: () => void;
  hideDropdown: () => void;
}

export default function BrandTemplateCardMenu({
  template,
  productLabel,
  design,
  mode,
  setMode,
  isCopyLoading,
  isDownloadFetching,
  onEdit,
  onCopy,
  onCopyToBrandKit,
  onDownloadClick,
  onRemoveFromBrandTemplates,
  onMakeLive,
  onRevertToDraft,
  onDelete,
  closeMenu,
  hideDropdown,
}: BrandTemplateCardMenuProps) {
  const ref = useRef(null);
  const [member] = useRecoilState(memberState);
  const [downloadType, setDownloadType] = useState(DownloadType.Standard);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [videoDuration, setVideoDuration] = useState(15);

  useOnClickOutside(ref, () => closeMenu());

  useEffect(() => {
    if (!design) return;
    let type = DownloadType.Standard;
    if (isFreeUser(member)) {
      type = DownloadType.BasicImage;
    } else if (isDigital(design) || isCustomSize(design)) {
      type = DownloadType.PremiumWeb;
    }
    setDownloadType(type);
  }, [member, design]);

  function changeDownloadType(type: DownloadType, ev: ReactMouseEvent) {
    if (design && isFreeUser(member) && !design.free) {
      if (PremiumDownloadTypes.includes(type)) {
        closeMenu();
        if (isDesignSubscription(member)) {
          window.MHM.ProModal.switchToProWithOverview({
            memberId: member.id,
            onSuccess: () => window.location.reload(),
          });
        } else {
          window.MHM.ProModal.upgrade({
            memberId: member.id,
            onSuccess: () => window.location.reload(),
          });
        }
        return;
      }
    }
    ev.stopPropagation();
    setDownloadType(type);
  }

  const canDownloadPDF = (d: DesignDto) => {
    if (isSocial(d)) return false;
    if (isCustomSize(d) && d.unit === "px") return false;
    return true;
  };

  async function download() {
    if (!design) {
      setDownloadLoading(false);
      return;
    }
    let call: Response | null = null;

    if (downloadType === DownloadType.Standard || downloadType === DownloadType.ProPrint) {
      await DesignApi.registerDownload(design.id!);
      call = await DesignApi.downloadPdf(design.id!, downloadType === DownloadType.ProPrint);
    } else if (downloadType === DownloadType.BasicImage || downloadType === DownloadType.PremiumWeb) {
      await DesignApi.registerDownload(design.id!);
      call = await DesignApi.downloadImage(design.id!, downloadType === DownloadType.PremiumWeb ? "png" : "jpg");
    } else if (downloadType === DownloadType.HDVideo) {
      call = await DesignApi.downloadVideo(design.id!, videoDuration);
    }

    if (call === null) {
      setDownloadLoading(false);
      return;
    }

    const blob = await call.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    // @ts-ignore - headers entries iterator
    a.download = getHeader([...call.headers], "filename");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setDownloadLoading(false);
    window.URL.revokeObjectURL(url);
    hideDropdown();
  }

  function startDownload(ev: ReactMouseEvent) {
    ev.stopPropagation();
    setDownloadLoading(true);
    download();
  }

  return (
    <div className={"ops-menu"} ref={ref}>
      <header>
        <h4 className={"mobile-display-name"}>{template.name}</h4>
        <h6>
          <span>{productLabel}</span>
          {template.lastEditorName && <span> &bull; By {template.lastEditorName}</span>}
        </h6>
      </header>

      {mode === MenuMode.Root && (
        <ul>
          <li className={"round-top"}>
            <Button type={"link"} onClick={onEdit}>
              <EditDesignIcon /> Edit This Template
            </Button>
          </li>
          <li>
            {template.visibility === "draft" ? (
              <Button type={"link"} onClick={onMakeLive}>
                <CheckCircleOutlined /> Make live
              </Button>
            ) : (
              <Button type={"link"} onClick={onRevertToDraft}>
                <RollbackOutlined /> Revert to draft
              </Button>
            )}
          </li>
          <li className={isCopyLoading ? "copy-loading" : ""}>
            <Button type={"link"} onClick={onCopy} disabled={isCopyLoading}>
              {isCopyLoading ? <LoadingOutlined /> : <CopyDesignIcon />} Make a copy
            </Button>
          </li>
          <li>
            <Button type={"link"} onClick={onCopyToBrandKit}>
              <CopyToBrandKitIcon /> Copy to other Brand Kit
            </Button>
          </li>
          <li>
            <Button type={"link"} onClick={onDownloadClick} disabled={isDownloadFetching}>
              {isDownloadFetching ? <LoadingOutlined /> : <DownloadDesignIcon />} Download
            </Button>
          </li>
          <li>
            <Button type={"link"} onClick={onRemoveFromBrandTemplates}>
              <MinusCircleOutlined /> Remove from Brand Templates
            </Button>
          </li>
          <li>
            <Button type={"link"} onClick={onDelete}>
              <DeleteDesignIcon /> Delete
            </Button>
          </li>
        </ul>
      )}

      {mode === MenuMode.Download && design && (
        <div className={"download-ops"}>
          <ul>
            {design.hasVideo && (
              <li>
                <Button type={"link"} onClick={(ev) => changeDownloadType(DownloadType.HDVideo, ev)}>
                  HD Video
                  {!isFreeUser(member) && downloadType === DownloadType.HDVideo && <CheckIcon />}
                  {isFreeUser(member) && <ProIcon />}
                </Button>

                {downloadType === DownloadType.HDVideo && (
                  <div className="slider">
                    <div>Duration</div>
                    <Slider
                      defaultValue={videoDuration}
                      value={videoDuration}
                      min={1}
                      max={30}
                      onChange={setVideoDuration}
                    />
                    <span>{videoDuration}s</span>
                  </div>
                )}
              </li>
            )}
            {canDownloadPDF(design) && (
              <li>
                <Button type={"link"} onClick={(ev) => changeDownloadType(DownloadType.Standard, ev)}>
                  Standard PDF
                  {(!isFreeUser(member) || design.free) && downloadType === DownloadType.Standard && <CheckIcon />}
                  {isFreeUser(member) && !design.free && <ProIcon />}
                </Button>
              </li>
            )}
            {canDownloadPDF(design) && !isDigiboard(design) && (
              <li>
                <Button type={"link"} onClick={(ev) => changeDownloadType(DownloadType.ProPrint, ev)}>
                  Pro Print PDF
                  {(!isFreeUser(member) || design.free) && downloadType === DownloadType.ProPrint && <CheckIcon />}
                  {isFreeUser(member) && !design.free && <ProIcon />}
                </Button>
              </li>
            )}
            <li>
              <Button type={"link"} onClick={(ev) => changeDownloadType(DownloadType.PremiumWeb, ev)}>
                Premium Web Image
                {(!isFreeUser(member) || design.free) && downloadType === DownloadType.PremiumWeb && <CheckIcon />}
                {isFreeUser(member) && !design.free && <ProIcon />}
              </Button>
            </li>
            <li>
              <Button type={"link"} onClick={(ev) => changeDownloadType(DownloadType.BasicImage, ev)}>
                Basic Image
                {isFreeUser(member) && !design.free && <span className={"free"}>Free</span>}
                {downloadType === DownloadType.BasicImage && <CheckIcon />}
              </Button>
            </li>
          </ul>

          <footer>
            <Button
              type={"primary"}
              className={"primary"}
              size={"large"}
              onClick={(ev) => startDownload(ev)}
              loading={downloadLoading}
            >
              Download
            </Button>
          </footer>
        </div>
      )}
    </div>
  );
}
